import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { ArrowLeft, Download, Inbox } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import DataTable from "../../components/dashboard/DataTable";
import ListPagination from "../../components/dashboard/ListPagination";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { canSeeReportAudience, getOpsReport } from "../../config/opsReports";
import usePermissions from "../../hooks/usePermissions";
import { fetchOpsReport } from "../../services/reportsApi";
import { exportReport } from "../../utils/exportReport";
import { showError } from "../../utils/toast";
import { splitPersonName } from "../../utils/personName";

const MISSING = "not worked on";

function withPersonColumns(columns, rows) {
  const named = columns.some((column) => column.key === "customer" || column.key === "customer_name");
  if (!named) return { columns, rows };
  return {
    columns: columns.flatMap((column) => {
      if (column.key !== "customer" && column.key !== "customer_name") return [column];
      return [
        { ...column, key: "first_name", label: "First name" },
        { ...column, key: "last_name", label: "Last name" },
      ];
    }),
    rows: rows.map((row) => {
      const parts = splitPersonName(row);
      return {
        ...row,
        first_name: parts.first === "—" ? null : parts.first,
        last_name: parts.last === "—" ? null : parts.last,
      };
    }),
  };
}

const EMPTY_FILTERS = {
  date_from: "",
  date_to: "",
  agent: "",
  manager: "",
  case_status: "",
  payment_status: "",
  payment_channel: "",
  escalation_status: "",
  period: "monthly",
  search: "",
  provider: "",
  portfolio_group: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
        {label}
      </span>
      {children}
    </label>
  );
}

function inputClass() {
  return "w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary";
}

function Select({ value, onChange, options, placeholder = "All" }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass()}>
      <option value="">{placeholder}</option>
      {options.map((option) => {
        const id = typeof option === "object" ? option.id : option;
        const name = typeof option === "object" ? option.name : option;
        return (
          <option key={id} value={id}>
            {name}
          </option>
        );
      })}
    </select>
  );
}

function formatCell(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" && (value.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(value))) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime()) && value.length >= 10) {
      return value.includes("T")
        ? date.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    }
  }
  return String(value);
}

export default function ReportDetailPage() {
  const { slug } = useParams();
  const { user } = usePermissions();
  const report = getOpsReport(slug, user);
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({});
  const [pagination, setPagination] = useState({ total: 0, last_page: 1, from: null, to: null });
  const [loading, setLoading] = useState(true);
  const [managerQuery, setManagerQuery] = useState("");
  const [providerQuery, setProviderQuery] = useState("");

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const enabled = report?.filters || [];

  const load = useCallback(
    async (nextPage = 1) => {
      if (!useLiveApi || !report) {
        setRows([]);
        setLoading(false);
        return;
      }
      setPage(nextPage);
      setRows([]);
      setColumns([]);
      setLoading(true);
      try {
        const data = await fetchOpsReport(token, report.slug, {
          ...applied,
          page: nextPage,
          limit: 25,
        });
        const shaped = withPersonColumns(
          Array.isArray(data.columns) ? data.columns : [],
          Array.isArray(data.rows) ? data.rows : []
        );
        setColumns(shaped.columns);
        setRows(shaped.rows);
        setOptions(data.filter_options || {});
        setPagination({
          total: Number(data.pagination?.total || 0),
          last_page: Number(data.pagination?.last_page || 1),
          current_page: Number(data.pagination?.current_page || nextPage),
          from: data.pagination?.from ?? null,
          to: data.pagination?.to ?? null,
        });
        setPage(Number(data.pagination?.current_page || nextPage));
      } catch (error) {
        showError(error.message || "Could not load report.");
      } finally {
        setLoading(false);
      }
    },
    [applied, report, token, useLiveApi]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const providerOptions = useMemo(() => {
    const list = Array.isArray(options.providers) ? options.providers : [];
    const byManager = filters.manager
      ? list.filter((provider) =>
          (provider.manager_ids || []).map(String).includes(String(filters.manager))
        )
      : list;
    const query = providerQuery.trim().toLowerCase();
    if (!query) return byManager;
    return byManager.filter((provider) => String(provider.name || "").toLowerCase().includes(query));
  }, [options.providers, filters.manager, providerQuery]);

  const groupOptions = useMemo(() => {
    const list = Array.isArray(options.portfolio_groups) ? options.portfolio_groups : [];
    const visibleProviderIds = new Set(
      (filters.provider
        ? (options.providers || []).filter((provider) => String(provider.id) === String(filters.provider))
        : filters.manager
          ? (options.providers || []).filter((provider) =>
              (provider.manager_ids || []).map(String).includes(String(filters.manager))
            )
          : options.providers || []
      ).map((provider) => String(provider.id))
    );
    return list.filter((group) => visibleProviderIds.has(String(group.provider_id)));
  }, [options.portfolio_groups, options.providers, filters.manager, filters.provider]);

  const managerOptions = useMemo(() => {
    const list = Array.isArray(options.managers) ? options.managers : [];
    const query = managerQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((manager) => String(manager.name || "").toLowerCase().includes(query));
  }, [options.managers, managerQuery]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    setPage(1);
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setManagerQuery("");
    setProviderQuery("");
    setPage(1);
  };

  const handleExport = async (format) => {
    let exportColumns = columns;
    let exportRows = rows;
    if (useLiveApi && report) {
      try {
        const data = await fetchOpsReport(token, report.slug, { ...applied, page: 1, limit: 5000 });
        if (Array.isArray(data.columns) && data.columns.length) exportColumns = data.columns;
        if (Array.isArray(data.rows)) exportRows = data.rows;
      } catch (error) {
        showError(error.message || "Could not export report.");
        return;
      }
    }
    const sheetRows = exportRows.map((row) => {
      const out = {};
      exportColumns.forEach((column) => {
        out[column.label || column.key] = row[column.key] ?? "";
      });
      return out;
    });
    const payloadRows = sheetRows.length
      ? sheetRows
      : [
          Object.fromEntries(
            (exportColumns.length ? exportColumns : [{ label: "Message", key: "message" }]).map((column) => [
              column.label || column.key,
              exportColumns.length ? "" : "No rows",
            ])
          ),
        ];
    exportReport(
      format,
      {
        title: report.name,
        type: report.groupLabel || "Report",
        period:
          applied.date_from || applied.date_to
            ? `${applied.date_from || "…"} to ${applied.date_to || "…"}`
            : "All dates",
        report: { Rows: payloadRows },
      },
      report.slug
    );
  };

  const activeFilterCount = useMemo(
    () =>
      Object.entries(applied).filter(([key, value]) => {
        if (key === "period" && value === "monthly") return false;
        return value !== "";
      }).length,
    [applied]
  );

  if (!report || !canSeeReportAudience(user, report.audience)) {
    return <Navigate to="/dashboard/reports" replace />;
  }

  return (
    <PageShell title={report.name} description={report.purpose}>
      <div className="mb-4">
        <Link
          to="/dashboard/reports"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary/55 hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All reports
        </Link>
      </div>

      <p className="mb-4 rounded-xl border border-primary/[0.07] bg-primary/[0.03] px-4 py-3 text-sm text-primary/65">
        {report.purpose}
      </p>

      <form
        onSubmit={applyFilters}
        className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="From">
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
              className={inputClass()}
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
              className={inputClass()}
            />
          </Field>
          {enabled.includes("period") ? (
            <Field label="Period">
              <select
                value={filters.period || "monthly"}
                onChange={(e) => setFilter("period", e.target.value || "monthly")}
                className={inputClass()}
              >
                {(options.period_grains || ["daily", "weekly", "monthly"]).map((grain) => (
                  <option key={grain} value={grain}>
                    {grain.charAt(0).toUpperCase() + grain.slice(1)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
          {enabled.includes("manager") ? (
            <Field label="Account manager">
              <input
                type="search"
                value={managerQuery}
                onChange={(e) => setManagerQuery(e.target.value)}
                placeholder="Search managers"
                className={`${inputClass()} mb-1.5`}
              />
              <Select
                value={filters.manager}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    manager: value,
                    provider: "",
                    portfolio_group: "",
                  }))
                }
                options={managerOptions}
                placeholder="All managers"
              />
            </Field>
          ) : null}
          {enabled.includes("provider") ? (
            <Field label="Provider">
              <input
                type="search"
                value={providerQuery}
                onChange={(e) => setProviderQuery(e.target.value)}
                placeholder="Search providers"
                className={`${inputClass()} mb-1.5`}
              />
              <Select
                value={filters.provider}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    provider: value,
                    portfolio_group: "",
                  }))
                }
                options={providerOptions}
                placeholder={filters.manager ? "This manager's providers" : "All providers"}
              />
            </Field>
          ) : null}
          {enabled.includes("portfolio_group") ? (
            <Field label="Portfolio group">
              <Select
                value={filters.portfolio_group}
                onChange={(value) => setFilter("portfolio_group", value)}
                options={groupOptions}
                placeholder="All uploaded groups"
              />
            </Field>
          ) : null}
          {enabled.includes("agent") ? (
            <Field label="Agent">
              <Select
                value={filters.agent}
                onChange={(value) => setFilter("agent", value)}
                options={options.agents || []}
                placeholder="All agents"
              />
            </Field>
          ) : null}
          {enabled.includes("case_status") ? (
            <Field label="Case status">
              <Select
                value={filters.case_status}
                onChange={(value) => setFilter("case_status", value)}
                options={options.case_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("payment_status") ? (
            <Field label="Payment status">
              <Select
                value={filters.payment_status}
                onChange={(value) => setFilter("payment_status", value)}
                options={options.payment_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("payment_channel") ? (
            <Field label="Payment channel">
              <Select
                value={filters.payment_channel}
                onChange={(value) => setFilter("payment_channel", value)}
                options={options.payment_channels || []}
              />
            </Field>
          ) : null}
          {enabled.includes("escalation_status") ? (
            <Field label="Escalation status">
              <Select
                value={filters.escalation_status}
                onChange={(value) => setFilter("escalation_status", value)}
                options={options.escalation_statuses || []}
              />
            </Field>
          ) : null}
          {enabled.includes("search") ? (
            <Field label="Search">
              <input
                type="search"
                value={filters.search}
                onChange={(e) => setFilter("search", e.target.value)}
                placeholder="Customer, case, account…"
                className={inputClass()}
              />
            </Field>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm">
            Apply filters
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
          {activeFilterCount > 0 ? (
            <span className="text-[11px] font-semibold text-primary/45">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
            </span>
          ) : null}
          <span className="ml-auto inline-flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => handleExport("Excel")}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Excel
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => handleExport("CSV")}>
              CSV
            </Button>
          </span>
        </div>
      </form>

      <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white shadow-[0_1px_3px_rgba(127,29,29,0.06)]">
        {loading && rows.length === 0 && columns.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">Loading report…</p>
        ) : columns.length === 0 && rows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/[0.05] text-primary/30">
              <Inbox className="h-6 w-6" />
            </span>
            <p className="mt-3 text-sm font-medium text-primary/50">No rows for this report yet.</p>
          </div>
        ) : (
          <DataTable
            key={`${slug}-${page}`}
            resetKey={`${slug}-${page}`}
            embedded
            paginate={false}
            compact
            columns={columns.map((column) => ({
              key: column.key,
              header: column.label,
              render: (row) => {
                const value = row[column.key];
                const text = value == null ? "" : String(value).trim();
                if (/^customer$/i.test(text)) {
                  return <span className="text-primary/35">—</span>;
                }
                const missing = !column.available || value === MISSING;
                return missing ? (
                  <span className="italic text-primary/35">{MISSING}</span>
                ) : (
                  formatCell(value)
                );
              },
            }))}
            data={rows}
            emptyMessage="No rows for these filters."
            getRowId={(row, index) =>
              String(
                row.case_id ||
                  row.payment_request_id ||
                  row.transaction_id ||
                  row.audit_id ||
                  row.ptp_id ||
                  row.escalation_id ||
                  row.user_id ||
                  row.agent_id ||
                  row.period ||
                  index
              )
            }
          />
        )}
        {pagination.last_page > 1 ? (
          <ListPagination
            page={page}
            totalPages={pagination.last_page}
            totalItems={pagination.total}
            startIndex={pagination.from || 0}
            endIndex={pagination.to || 0}
            goToPage={(next) => load(next)}
            alwaysShow
            pageSize={25}
          />
        ) : null}
      </div>
    </PageShell>
  );
}
